import { useEffect, useRef, useState } from 'react';
import { getNiche } from '@/services/mockData';
import { DetailShell } from '../../detail/DetailShell';
import { SectionTitle } from '../../detail/SectionTitle';
import { useVideoPopup } from '../../detail/VideoPopup';
import { NicheMap } from './NicheMap';

const SECTOR_SWATCHES = ['#D7C4A7', '#C4D6D4', '#E5C8C0', '#C9D1B9', '#CBC3DC'];

function HomeRow({ home }) {
  if (!home) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
      padding: '6px 10px', background: 'var(--paper-2)', border: '1px dashed var(--ink-3)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          width: 10, height: 10, borderRadius: '50%',
          background: 'var(--accent)', border: '1.5px solid var(--ink)', display: 'inline-block',
        }} />
        <span className="mono" style={{ fontSize: 10, letterSpacing: '0.1em', color: 'var(--ink-3)' }}>HOME BASE</span>
        <span style={{ fontFamily: 'var(--serif)', fontSize: 16 }}>{home.city}</span>
      </div>
      <span className="mono" style={{ fontSize: 10, letterSpacing: '0.1em', color: 'var(--ink-3)' }}>
        {home.lat.toFixed(2)}°, {home.lon.toFixed(2)}°
      </span>
    </div>
  );
}

const PRIORITY_COLOR = {
  primary:   'var(--ink)',
  secondary: 'var(--accent)',
  tertiary:  'var(--ink-4)',
};

const PRIORITY_CYCLE = { primary: 'secondary', secondary: 'tertiary', tertiary: 'primary' };

const EYE = '01 · Niche Definition';

// ─── Shared add-item row ──────────────────────────────────────────────────────

function AddItemRow({ placeholder, onSave, onCancel }) {
  const [val, setVal] = useState('');
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  function handleSubmit(e) {
    e.preventDefault();
    const trimmed = val.trim();
    if (!trimmed) return;
    onSave(trimmed);
  }
  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 8, marginTop: 10 }}>
      <input
        ref={ref}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder={placeholder}
        style={{
          flex: 1, background: 'var(--paper)', border: '1px solid var(--ink)',
          padding: '6px 10px', fontFamily: 'var(--sans)', fontSize: 13,
          color: 'var(--ink)', outline: 'none',
        }}
        onFocus={(e) => (e.target.style.borderColor = 'var(--accent)')}
        onBlur={(e) => (e.target.style.borderColor = 'var(--ink)')}
      />
      <button
        type="submit"
        style={{
          fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em',
          textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--paper)',
          border: '1px solid var(--ink)', padding: '6px 12px', cursor: 'pointer',
        }}
      >Add</button>
      <button
        type="button"
        onClick={onCancel}
        style={{
          fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em',
          textTransform: 'uppercase', background: 'transparent', color: 'var(--ink-3)',
          border: '1px solid var(--hairline)', padding: '6px 12px', cursor: 'pointer',
        }}
      >Cancel</button>
    </form>
  );
}

// ─── Add ring row ─────────────────────────────────────────────────────────────

function AddRingRow({ onSave, onCancel }) {
  const [label, setLabel] = useState('');
  const [km, setKm] = useState('');
  const [priority, setPriority] = useState('secondary');
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  function handleSubmit(e) {
    e.preventDefault();
    const trimmedLabel = label.trim();
    const parsedKm = parseInt(km, 10);
    if (!trimmedLabel || !parsedKm || parsedKm <= 0) return;
    onSave(trimmedLabel, parsedKm, priority);
  }
  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
      <input
        ref={ref}
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Label (e.g. Overnight)"
        style={{
          flex: 2, minWidth: 100, background: 'var(--paper)', border: '1px solid var(--ink)',
          padding: '6px 10px', fontFamily: 'var(--sans)', fontSize: 13,
          color: 'var(--ink)', outline: 'none',
        }}
        onFocus={(e) => (e.target.style.borderColor = 'var(--accent)')}
        onBlur={(e) => (e.target.style.borderColor = 'var(--ink)')}
      />
      <input
        type="number"
        value={km}
        onChange={(e) => setKm(e.target.value)}
        placeholder="km"
        min="1"
        style={{
          width: 72, background: 'var(--paper)', border: '1px solid var(--ink)',
          padding: '6px 10px', fontFamily: 'var(--mono)', fontSize: 13,
          color: 'var(--ink)', outline: 'none',
        }}
        onFocus={(e) => (e.target.style.borderColor = 'var(--accent)')}
        onBlur={(e) => (e.target.style.borderColor = 'var(--ink)')}
      />
      <select
        value={priority}
        onChange={(e) => setPriority(e.target.value)}
        style={{
          fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em',
          textTransform: 'uppercase', background: 'var(--paper)', color: 'var(--ink)',
          border: '1px solid var(--ink)', padding: '6px 10px', cursor: 'pointer', outline: 'none',
        }}
      >
        <option value="primary">Primary</option>
        <option value="secondary">Secondary</option>
        <option value="tertiary">Tertiary</option>
      </select>
      <button
        type="submit"
        style={{
          fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em',
          textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--paper)',
          border: '1px solid var(--ink)', padding: '6px 12px', cursor: 'pointer',
        }}
      >Add</button>
      <button
        type="button"
        onClick={onCancel}
        style={{
          fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em',
          textTransform: 'uppercase', background: 'transparent', color: 'var(--ink-3)',
          border: '1px solid var(--hairline)', padding: '6px 12px', cursor: 'pointer',
        }}
      >Cancel</button>
    </form>
  );
}

// ─── Add region row ───────────────────────────────────────────────────────────

function AddRegionRow({ onSave, onCancel }) {
  const [name, setName] = useState('');
  const [priority, setPriority] = useState('secondary');
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  function handleSubmit(e) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    onSave(trimmed, priority);
  }
  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 8, marginTop: 10 }}>
      <input
        ref={ref}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Country or region"
        style={{
          flex: 1, background: 'var(--paper)', border: '1px solid var(--ink)',
          padding: '6px 10px', fontFamily: 'var(--sans)', fontSize: 13,
          color: 'var(--ink)', outline: 'none',
        }}
        onFocus={(e) => (e.target.style.borderColor = 'var(--accent)')}
        onBlur={(e) => (e.target.style.borderColor = 'var(--ink)')}
      />
      <select
        value={priority}
        onChange={(e) => setPriority(e.target.value)}
        style={{
          fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em',
          textTransform: 'uppercase', background: 'var(--paper)', color: 'var(--ink)',
          border: '1px solid var(--ink)', padding: '6px 10px', cursor: 'pointer', outline: 'none',
        }}
      >
        <option value="primary">Primary</option>
        <option value="secondary">Secondary</option>
        <option value="tertiary">Tertiary</option>
      </select>
      <button
        type="submit"
        style={{
          fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em',
          textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--paper)',
          border: '1px solid var(--ink)', padding: '6px 12px', cursor: 'pointer',
        }}
      >Add</button>
      <button
        type="button"
        onClick={onCancel}
        style={{
          fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em',
          textTransform: 'uppercase', background: 'transparent', color: 'var(--ink-3)',
          border: '1px solid var(--hairline)', padding: '6px 12px', cursor: 'pointer',
        }}
      >Cancel</button>
    </form>
  );
}

// ─── Shared add button ────────────────────────────────────────────────────────

function AddBtn({ onClick, label = '+ Add' }) {
  return (
    <button
      onClick={onClick}
      style={{
        marginTop: 10, fontFamily: 'var(--mono)', fontSize: 10,
        letterSpacing: '0.08em', textTransform: 'uppercase',
        background: 'transparent', border: '1px dashed var(--ink-3)',
        color: 'var(--ink-3)', padding: '6px 14px', cursor: 'pointer', width: '100%',
      }}
    >
      {label}
    </button>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function NicheDetail({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [geoMode, setGeoMode] = useState('countries');
  const [addingRole, setAddingRole] = useState(false);
  const [addingSector, setAddingSector] = useState(false);
  const [addingRegion, setAddingRegion] = useState(false);
  const [addingRing, setAddingRing] = useState(false);
  const vp = useVideoPopup();

  useEffect(() => {
    let cancelled = false;
    getNiche()
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  // ── Roles ──────────────────────────────────────────────────────────────────
  function handleSetPrimary(role) {
    setData((prev) => ({ ...prev, primary_role: role }));
  }
  function handleRemoveRole(role) {
    setData((prev) => {
      const roles = prev.roles.filter((r) => r !== role);
      return { ...prev, roles, primary_role: prev.primary_role === role ? (roles[0] ?? '') : prev.primary_role };
    });
  }
  function handleAddRole(name) {
    setData((prev) => ({ ...prev, roles: [...prev.roles, name] }));
    setAddingRole(false);
  }

  // ── Life stages ────────────────────────────────────────────────────────────
  function handleToggleLifeStage(stage) {
    setData((prev) => ({
      ...prev,
      life_stages: prev.life_stages.includes(stage)
        ? prev.life_stages.filter((s) => s !== stage)
        : [...prev.life_stages, stage],
    }));
  }

  // ── Sizes ──────────────────────────────────────────────────────────────────
  function handleToggleSize(size) {
    setData((prev) => ({
      ...prev,
      sizes: prev.sizes.includes(size)
        ? prev.sizes.filter((s) => s !== size)
        : [...prev.sizes, size],
    }));
  }

  // ── Sectors ────────────────────────────────────────────────────────────────
  function handleRemoveSector(sector) {
    setData((prev) => ({ ...prev, sectors: prev.sectors.filter((s) => s !== sector) }));
  }
  function handleAddSector(name) {
    setData((prev) => ({ ...prev, sectors: [...prev.sectors, name] }));
    setAddingSector(false);
  }

  // ── Regions ────────────────────────────────────────────────────────────────
  function handleRegionNote(id, note) {
    setData((prev) => ({
      ...prev,
      geo: { ...prev.geo, regions: prev.geo.regions.map((r) => r.id === id ? { ...r, note } : r) },
    }));
  }
  function handleCycleRegionPriority(id) {
    setData((prev) => ({
      ...prev,
      geo: {
        ...prev.geo,
        regions: prev.geo.regions.map((r) =>
          r.id === id ? { ...r, priority: PRIORITY_CYCLE[r.priority] ?? 'primary' } : r
        ),
      },
    }));
  }
  function handleRemoveRegion(id) {
    setData((prev) => ({
      ...prev,
      geo: { ...prev.geo, regions: prev.geo.regions.filter((r) => r.id !== id) },
    }));
  }
  function handleAddRegion(name, priority) {
    setData((prev) => ({
      ...prev,
      geo: {
        ...prev.geo,
        regions: [...prev.geo.regions, { id: `r${Date.now()}`, name, priority, note: '' }],
      },
    }));
    setAddingRegion(false);
  }

  // ── Rings ──────────────────────────────────────────────────────────────────
  function handleRingNote(label, note) {
    setData((prev) => ({
      ...prev,
      geo: { ...prev.geo, rings: prev.geo.rings.map((r) => r.label === label ? { ...r, note } : r) },
    }));
  }
  function handleCycleRingPriority(label) {
    setData((prev) => ({
      ...prev,
      geo: {
        ...prev.geo,
        rings: prev.geo.rings.map((r) =>
          r.label === label ? { ...r, priority: PRIORITY_CYCLE[r.priority] ?? 'primary' } : r
        ),
      },
    }));
  }
  function handleRemoveRing(label) {
    setData((prev) => ({
      ...prev,
      geo: { ...prev.geo, rings: prev.geo.rings.filter((r) => r.label !== label) },
    }));
  }
  function handleAddRing(label, km, priority) {
    setData((prev) => {
      const rings = [...prev.geo.rings, { km, priority, label, note: '' }]
        .sort((a, b) => a.km - b.km);
      return { ...prev, geo: { ...prev.geo, rings } };
    });
    setAddingRing(false);
  }

  return (
    <DetailShell eyebrow="01 · Proposition Builder" title="Niche Definition" onClose={onClose}>
      {loading && <div className="tiny" style={{ color: 'var(--ink-3)' }}>Loading…</div>}
      {error && <div className="tiny" style={{ color: 'var(--danger)' }}>Could not load niche</div>}
      {data && (
        <>
          {/* ── Fractional roles ── */}
          <div className="card">
            <SectionTitle
              label="Fractional roles"
              meta={`${data.roles.length} TARGETED`}
              onPlay={vp.open}
              video={{
                eyebrow: EYE,
                title: "Choosing the fractional roles you'll own",
                chapter: '01', duration: '02:24',
                description: 'Why most fractional execs target one primary role and protect the edges. How to pick yours, and what it means for who hires you.',
                chips: ['Positioning', 'Primary vs. secondary', 'Day-rate logic'],
              }}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {data.roles.map((r, i) => {
                const primary = r === data.primary_role;
                return (
                  <div
                    key={r}
                    className="group"
                    onClick={() => handleSetPrimary(r)}
                    title="Click to set as primary"
                    style={{
                      border: '1px solid var(--ink)', padding: '10px 14px',
                      background: primary ? 'var(--ink)' : 'var(--paper)',
                      color: primary ? 'var(--paper)' : 'var(--ink)',
                      display: 'flex', alignItems: 'baseline', gap: 10,
                      cursor: 'pointer', position: 'relative',
                    }}
                  >
                    <span className="mono" style={{ fontSize: 10, opacity: 0.6 }}>{String(i + 1).padStart(2, '0')}</span>
                    <span style={{ fontFamily: 'var(--serif)', fontSize: 20, letterSpacing: '-0.01em' }}>{r}</span>
                    {primary && (
                      <span className="mono" style={{ fontSize: 10, letterSpacing: '0.08em', opacity: 0.7 }}>PRIMARY</span>
                    )}
                    <button
                      className="opacity-0 group-hover:opacity-100"
                      onClick={(e) => { e.stopPropagation(); handleRemoveRole(r); }}
                      style={{
                        marginLeft: 'auto', fontSize: 16, lineHeight: 1, paddingLeft: 6,
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: primary ? 'var(--paper)' : 'var(--ink-3)',
                        transition: 'opacity 0.1s',
                      }}
                      title="Remove role"
                    >×</button>
                  </div>
                );
              })}
            </div>
            {addingRole
              ? <AddItemRow placeholder="Role name (e.g. Fractional CMO)" onSave={handleAddRole} onCancel={() => setAddingRole(false)} />
              : <AddBtn onClick={() => setAddingRole(true)} label="+ Add role" />
            }
          </div>

          {/* ── Life stages + headcount ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="card">
              <SectionTitle
                label="Life stages"
                meta={`${data.life_stages.length} OF ${data.all_life_stages.length}`}
                onPlay={vp.open}
                video={{
                  eyebrow: EYE,
                  title: 'The seven life stages — where you actually help',
                  chapter: '02', duration: '03:05',
                  description: 'From Startup to Turnaround. Which stages match your golden threads, and why claiming all seven is the fastest way to be hired by none.',
                  chips: ['Stage fit', 'Scale-up', 'Renewal', 'Turnaround'],
                }}
              />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0, border: '1px solid var(--ink)' }}>
                {data.all_life_stages.map((s, i) => {
                  const on = data.life_stages.includes(s);
                  const def = data.life_stage_defs && data.life_stage_defs[s];
                  return (
                    <div
                      key={s}
                      className="lifestage-cell"
                      onClick={() => handleToggleLifeStage(s)}
                      style={{
                        padding: '14px 6px 12px', textAlign: 'center',
                        borderLeft: i === 0 ? '0' : '1px solid var(--hairline)',
                        background: on ? 'var(--ink)' : 'var(--paper)',
                        color: on ? 'var(--paper)' : 'var(--ink-4)',
                        minHeight: 110,
                        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                        cursor: 'pointer',
                      }}
                    >
                      <div className="mono" style={{ fontSize: 9, letterSpacing: '0.1em', opacity: 0.6 }}>0{i + 1}</div>
                      <div style={{ fontFamily: 'var(--serif)', fontSize: 13, lineHeight: 1.1, marginTop: 8 }}>{s}</div>
                      <div className="mono" style={{ fontSize: 9, letterSpacing: '0.1em', marginTop: 8, opacity: on ? 0.85 : 0.4 }}>
                        {on ? '●' : '○'}
                      </div>
                      {def && (
                        <div className={`lifestage-tip ${i >= 5 ? 'tip-left' : i === 0 ? 'tip-right' : 'tip-center'}`} role="tooltip">
                          <div className="lifestage-tip-eyebrow mono">0{i + 1} · {s}</div>
                          <div className="lifestage-tip-body">{def}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="card">
              <SectionTitle
                label="Business sizes"
                meta={`HEADCOUNT · ${data.sizes.length} OF ${data.all_sizes.length}`}
                onPlay={vp.open}
                video={{
                  eyebrow: EYE,
                  title: 'Headcount brackets — the right size of pain',
                  chapter: '03', duration: '02:11',
                  description: "Why fractional work is sharpest in the 11–50 band, and how to handle inbound from outside your bracket without losing your week.",
                  chips: ['1–10', '11–50', '51–200', 'Capacity'],
                }}
              />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${data.all_sizes.length}, 1fr)`,
                  gap: 8, alignItems: 'end', height: 120,
                }}
              >
                {data.all_sizes.map((s, i) => {
                  const on = data.sizes.includes(s);
                  const heights = [22, 38, 65, 82, 100];
                  return (
                    <div
                      key={s}
                      onClick={() => handleToggleSize(s)}
                      title={on ? 'Click to deselect' : 'Click to select'}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, height: '100%', justifyContent: 'end', cursor: 'pointer' }}
                    >
                      <div
                        style={{
                          width: '100%', height: `${heights[i]}%`,
                          border: '1px solid var(--ink)',
                          background: on ? 'var(--ink)' : 'var(--paper)',
                          position: 'relative',
                          transition: 'background 0.1s',
                        }}
                      >
                        {on && (
                          <div style={{
                            position: 'absolute', top: 6, left: 0, right: 0, textAlign: 'center',
                            fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.1em', color: 'var(--paper)',
                          }}>
                            ON
                          </div>
                        )}
                      </div>
                      <div className="mono" style={{ fontSize: 10, color: on ? 'var(--ink)' : 'var(--ink-4)' }}>{s}</div>
                    </div>
                  );
                })}
              </div>
              <div className="tiny" style={{ marginTop: 10, color: 'var(--ink-3)' }}>
                Click a bar to toggle · Bar height = headcount band
              </div>
            </div>
          </div>

          {/* ── Sectors ── */}
          <div className="card">
            <SectionTitle
              label="Sectors"
              meta={`${data.sectors.length} TARGETED`}
              onPlay={vp.open}
              video={{
                eyebrow: EYE,
                title: 'Sectors — narrow enough to be remembered',
                chapter: '04', duration: '02:42',
                description: 'How to pick the 3–5 sectors where your stories land. Use adjacent sectors as growth, not as a hedge.',
                chips: ['Reference clients', 'Adjacency', 'Story fit'],
              }}
            />
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${data.sectors.length || 1}, 1fr)`, gap: 10 }}>
              {data.sectors.map((s, i) => (
                <div
                  key={s}
                  className="group"
                  style={{
                    border: '1px solid var(--ink)', padding: '22px 16px',
                    background: SECTOR_SWATCHES[i % SECTOR_SWATCHES.length],
                    minHeight: 110, display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                    position: 'relative',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div className="mono" style={{ fontSize: 10, letterSpacing: '0.1em', color: 'var(--ink-2)' }}>
                      SECTOR · {String(i + 1).padStart(2, '0')}
                    </div>
                    <button
                      className="opacity-0 group-hover:opacity-100"
                      onClick={() => handleRemoveSector(s)}
                      style={{
                        fontSize: 16, lineHeight: 1, background: 'transparent',
                        border: 'none', cursor: 'pointer', color: 'var(--ink-3)',
                        transition: 'opacity 0.1s',
                      }}
                      title="Remove sector"
                    >×</button>
                  </div>
                  <div style={{ fontFamily: 'var(--serif)', fontSize: 24, letterSpacing: '-0.01em', lineHeight: 1.05 }}>{s}</div>
                </div>
              ))}
            </div>
            {addingSector
              ? <AddItemRow placeholder="Sector name (e.g. PropTech)" onSave={handleAddSector} onCancel={() => setAddingSector(false)} />
              : <AddBtn onClick={() => setAddingSector(true)} label="+ Add sector" />
            }
          </div>

          {/* ── Geography ── */}
          <div className="card">
            <SectionTitle
              label="Geography"
              meta={`BASE · ${(data.geo.home?.city ?? data.geo.base_city ?? 'HOME').toUpperCase()}`}
              onPlay={vp.open}
              video={{
                eyebrow: EYE,
                title: 'Geography — primary, secondary, tertiary',
                chapter: '05', duration: '02:58',
                description: "Travel days are the silent tax on fractional work. How to draw your map, and when 'open to remote' is the wrong answer.",
                chips: ['Travel cost', 'Time zones', 'Network density'],
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '-4px 0 12px' }}>
              <div role="tablist" style={{ display: 'inline-flex', border: '1px solid var(--ink)' }}>
                {[
                  { k: 'countries', label: 'By country' },
                  { k: 'radius',    label: 'By distance' },
                ].map((t) => {
                  const active = geoMode === t.k;
                  return (
                    <button key={t.k} role="tab" aria-selected={active}
                      onClick={() => setGeoMode(t.k)}
                      style={{
                        padding: '6px 10px', fontFamily: 'var(--mono)', fontSize: 10,
                        letterSpacing: '0.1em', textTransform: 'uppercase', border: 'none', cursor: 'pointer',
                        background: active ? 'var(--ink)' : 'transparent',
                        color: active ? 'var(--paper)' : 'var(--ink-2)',
                        borderLeft: t.k === 'radius' ? '1px solid var(--ink)' : 'none',
                      }}>
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16, alignItems: 'stretch' }}>
              <div style={{ position: 'relative', border: '1px solid var(--ink)', background: 'var(--paper-2)', aspectRatio: '4/3', overflow: 'hidden' }}>
                <NicheMap geo={data.geo} mode={geoMode} />
              </div>
              <div style={{ display: 'grid', gap: 8, alignContent: 'start' }}>
                <HomeRow home={data.geo.home} />

                {geoMode === 'countries' && (
                  <>
                    {data.geo.regions.map((r) => (
                      <div
                        key={r.id || r.name}
                        className="group"
                        style={{ borderLeft: `3px solid ${PRIORITY_COLOR[r.priority]}`, padding: '6px 10px', display: 'grid', gap: 2 }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontFamily: 'var(--serif)', fontSize: 17 }}>
                            {r.name}
                            {r.name === data.geo.home?.country && (
                              <span className="mono" style={{ fontSize: 9, letterSpacing: '0.1em', color: 'var(--ink-3)', marginLeft: 8 }}>HOME</span>
                            )}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                            <button
                              onClick={() => handleCycleRegionPriority(r.id)}
                              className="mono"
                              style={{
                                fontSize: 10, letterSpacing: '0.1em', color: 'var(--ink-3)',
                                background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
                              }}
                              title="Click to cycle priority"
                            >
                              {r.priority.toUpperCase()}
                            </button>
                            <button
                              className="opacity-0 group-hover:opacity-100 mono"
                              onClick={() => handleRemoveRegion(r.id)}
                              style={{
                                fontSize: 16, lineHeight: 1, background: 'transparent',
                                border: 'none', cursor: 'pointer', color: 'var(--ink-4)',
                                transition: 'opacity 0.1s', padding: 0,
                              }}
                              title="Remove region"
                            >×</button>
                          </div>
                        </div>
                        <input
                          value={r.note || ''}
                          onChange={(e) => handleRegionNote(r.id, e.target.value)}
                          placeholder="Add note…"
                          style={{
                            fontSize: 12, color: 'var(--ink-3)', background: 'transparent',
                            border: 'none', borderBottom: '1px solid transparent', outline: 'none',
                            width: '100%', padding: 0, fontFamily: 'var(--sans)',
                          }}
                          onFocus={(e) => (e.target.style.borderBottomColor = 'var(--hairline)')}
                          onBlur={(e) => (e.target.style.borderBottomColor = 'transparent')}
                        />
                      </div>
                    ))}
                    {addingRegion
                      ? <AddRegionRow onSave={handleAddRegion} onCancel={() => setAddingRegion(false)} />
                      : <AddBtn onClick={() => setAddingRegion(true)} label="+ Add region" />
                    }
                  </>
                )}

                {geoMode === 'radius' && (
                  <>
                    {(data.geo.rings ?? []).map((ring, i) => {
                      const prev = i === 0 ? 0 : data.geo.rings[i - 1].km;
                      const range = i === 0 ? `0–${ring.km} km` : `${prev}–${ring.km} km`;
                      return (
                        <div
                          key={ring.label}
                          className="group"
                          style={{ borderLeft: `3px solid ${PRIORITY_COLOR[ring.priority]}`, padding: '6px 10px', display: 'grid', gap: 2 }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontFamily: 'var(--serif)', fontSize: 17 }}>{ring.label}</span>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                              <button
                                onClick={() => handleCycleRingPriority(ring.label)}
                                className="mono"
                                style={{
                                  fontSize: 10, letterSpacing: '0.1em', color: 'var(--ink-3)',
                                  background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
                                }}
                                title="Click to cycle priority"
                              >
                                {ring.priority.toUpperCase()}
                              </button>
                              <button
                                className="opacity-0 group-hover:opacity-100 mono"
                                onClick={() => handleRemoveRing(ring.label)}
                                style={{
                                  fontSize: 16, lineHeight: 1, background: 'transparent',
                                  border: 'none', cursor: 'pointer', color: 'var(--ink-4)',
                                  transition: 'opacity 0.1s', padding: 0,
                                }}
                                title="Remove distance ring"
                              >×</button>
                            </div>
                          </div>
                          <div className="mono" style={{ fontSize: 10, letterSpacing: '0.06em', color: 'var(--ink-4)' }}>{range}</div>
                          <input
                            value={ring.note || ''}
                            onChange={(e) => handleRingNote(ring.label, e.target.value)}
                            placeholder="Add note…"
                            style={{
                              fontSize: 12, color: 'var(--ink-3)', background: 'transparent',
                              border: 'none', borderBottom: '1px solid transparent', outline: 'none',
                              width: '100%', padding: 0, fontFamily: 'var(--sans)',
                            }}
                            onFocus={(e) => (e.target.style.borderBottomColor = 'var(--hairline)')}
                            onBlur={(e) => (e.target.style.borderBottomColor = 'transparent')}
                          />
                        </div>
                      );
                    })}
                    {addingRing
                      ? <AddRingRow onSave={handleAddRing} onCancel={() => setAddingRing(false)} />
                      : <AddBtn onClick={() => setAddingRing(true)} label="+ Add distance" />
                    }
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      {vp.render()}
    </DetailShell>
  );
}

export default NicheDetail;
